import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from "@angular/core";
import { ECharts, EChartsOption } from "echarts";
import { StorageService } from "../../routes/services/storage.service";
import { ViewportService } from "../../core/viewport/viewport.service";

@Component({
  selector: "app-echar",
  templateUrl: "./echar.html",
  styleUrls: ["./echar.scss"]
})
export class EchartComponent implements OnInit {
  @Input() labels: any;
  @Input() data: any;
  @Input() height: number = 100;
  @Input() heightStyle: "px" | "%" = "%";
  @Input() type: any = "other";
  @Input() options: any = null;
  @Input() smallChart: any = false;

  @Input() reload: Boolean = false;
  computedHeight: string = "";
  echartsInstance: ECharts | undefined;

  chartOption: EChartsOption = {
    toolbox: {
      orient: "vertical",
      feature: {
        dataView: {},
        dataZoom: {},
        restore: {},
        magicType: { type: ["line", "bar"] },
        saveAsImage: {}
      }
      /*   tooltip: {
          formatter: function (param) {
            return '<div>' + param.title + '</div>'; // user-defined DOM structure
          },
          show: true,
          extraCssText: 'box-shadow: 0 0 3px rgba(0, 0, 0, 0.3);'
        } */
    },
    grid: {
      left: "3%",
      top: "8%",
      right: "4%",
      bottom: "3%",
      containLabel: true
    },
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "cross"
        /*    crossStyle: {
          color: "#999",
        }, */
      }
    },
    legend: {},
    xAxis: {
      type: "category",
      data: [],
      axisPointer: {
        type: "shadow"
      }
    },
    yAxis: [
      {
        type: "value"
      }
    ],
    series: []
  };
  @Output("clickChart") clickChart = new EventEmitter();

  constructor(public storageService: StorageService, private viewport: ViewportService) {}
  reloadGraph() {}

  /**
   * Indica si el tema activo es una variante oscura (E, F, G, H). Se lee en
   * vivo (no se cachea) porque storageService.localValue.theme llega de forma
   * asíncrona desde el backend y puede no estar listo todavía cuando este
   * componente se construye.
   * @returns true si el tema actual es oscuro.
   */
  get isDarkTheme(): boolean {
    const { theme } = this.storageService.localValue;
    return theme === "E" || theme === "F" || theme === "G" || theme === "H";
  }

  /**
   * Superpone color de texto blanco sobre leyenda y ejes cuando el tema
   * activo es oscuro. ECharts pinta en canvas, no responde a CSS, así que
   * el color debe venir en la config de la gráfica. Aplica tanto a las
   * opciones armadas internamente como a las recibidas por [options].
   * @param option Configuración de ECharts ya armada.
   * @returns La misma configuración, con textos en blanco si el tema es oscuro.
   */
  private withDarkTextStyle(option: EChartsOption): EChartsOption {
    if (!this.isDarkTheme) {
      return option;
    }

    const withWhiteAxisLabel = (axis: any) => {
      if (!axis) return axis;
      const apply = (item: any) => ({ ...item, axisLabel: { ...item.axisLabel, color: "#fff" } });
      return Array.isArray(axis) ? axis.map(apply) : apply(axis);
    };
    const withWhiteLegendText = (legend: any) => {
      if (!legend) return { textStyle: { color: "#fff" } };
      const apply = (item: any) => ({
        ...item,
        textStyle: { ...item.textStyle, color: "#fff" }
      });
      return Array.isArray(legend) ? legend.map(apply) : apply(legend);
    };

    return {
      ...option,
      legend: withWhiteLegendText(option.legend),
      xAxis: withWhiteAxisLabel(option.xAxis),
      yAxis: withWhiteAxisLabel(option.yAxis)
    };
  }

  /**
   * Ajustes para telefono (menos de 768px): oculta la barra de herramientas
   * (zoom, descargar, etc.), que en pantallas angostas se encima con la leyenda,
   * y deja la leyenda en una sola linea desplazable en vez de varias filas.
   * @param option Configuracion de ECharts ya armada.
   * @returns La misma configuracion, adaptada si la pantalla es de telefono.
   */
  private withMobileLayout(option: EChartsOption): EChartsOption {
    if (!this.viewport.isMobile) {
      return option;
    }
    const asScroll = (legend: any) => ({ ...legend, type: "scroll" });
    const legend: any = option.legend;
    const toolbox: any = option.toolbox;

    return {
      ...option,
      toolbox: Array.isArray(toolbox)
        ? toolbox.map((item) => ({ ...item, show: false }))
        : { ...toolbox, show: false },
      legend: Array.isArray(legend) ? legend.map(asScroll) : legend ? asScroll(legend) : legend
    };
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.height || changes.heightStyle) {
      this.computedHeight = this.height + this.heightStyle;
    }
    if (changes.reload) {
      this.echartsInstance && this.setValueChart(this.labels, this.data);
    } else if (this.type !== "diet") {
      this.echartsInstance && this.setValueChart(this.labels, this.data);
    } else if (changes.firstChange) {
      this.setValueChart(this.labels, this.data);
    }
    if (changes.options && !changes.firstChange && this.echartsInstance) {
      if (this.echartsInstance) {
        this.actualizarGrafico();
      }
    }
  }
  ngOnInit(): void {}

  onChartInit(ec: any) {
    this.echartsInstance = ec;
    /* this.echartsInstance.showLoading(); */

    this.setValueChart(this.labels, this.data);
    /*   setTimeout(() => {
      this.echartsInstance.hideLoading();
    }, 600); */
  }

  validateMinMax(value: any) {
    let obj = {};

    if (value && value.length === 1) {
      const { data } = value[0];
      obj = {
        max: Math.max(...data),
        min: Math.min(...data)
      };
    }

    return obj;
  }
  setValueChart(labels: any, data: any) {
    if (this.echartsInstance) {
      this.echartsInstance.clear();
    }
    if (this.options) {
      this.actualizarGrafico();
    } else {
      this.chartOption = this.withMobileLayout(this.withDarkTextStyle({
        ...this.chartOption,
        legend: {
          // Try 'horizontal'
          show: this.smallChart ? false : true,
          orient: "horizontal",
          top: 0
        },
        toolbox: {
          show: this.smallChart ? false : true
        },
        yAxis: [
          {
            type: "value",
            ...this.validateMinMax(data),
            min: 0,
            axisLabel: {
              formatter: "{value}"
            }
          }
        ],
        xAxis: {
          type: "category",
          show: this.smallChart ? false : true,
          data: labels,
          axisPointer: {
            type: "shadow"
          }
        },

        series:
          data && data.length > 0
            ? data.map((item: any) => {
                return {
                  ...item,
                  tooltip: {
                    valueFormatter: (value: any) => {
                      return value;
                    }
                  }
                };
              })
            : []
      }));
    }
  }
  chartClick(e: any) {
    this.clickChart.emit(e);
  }
  chartRestore(e: any) {
    this.setValueChart(this.labels, this.data);
  }
  gaugeClicked(e: any) {}
  chartDblClick(e: any) {}
  actualizarGrafico() {
    // Limpia el gráfico antes de actualizar las opciones
    if (this.echartsInstance) {
      this.echartsInstance.clear();

      this.chartOption = this.withMobileLayout(
        this.withDarkTextStyle({
          ...this.options
        })
      );
      // Asigna las nuevas opciones
      this.echartsInstance.setOption(this.chartOption);
    }
  }
}
